import {
  carteValide,
  formaterJour,
  formaterMontant,
  libelleCarte,
  pieceCorrection,
} from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { LogoConnu } from '../composants/Logo'
import { api, corps, ErreurApi, lire, requeteMoi, requetes, type VuePieces } from '../api'
import { ApercuDocument } from '../composants/ApercuPiece'
import { AssistantPiece } from '../composants/AssistantPiece'
import { EnLigne } from '../composants/EnLigne'
import { FilDrivePiece } from '../composants/FilDrive'
import { FilsPiece } from '../composants/FilsPiece'
import { CopierLien } from '../composants/CopierLien'
import { NavigationFile, useFilePieces } from '../composants/FileParcours'
import type { Champ } from '../composants/Formulaire'
import { formaterInstant, Historique } from '../composants/Historique'
import { PaiementDepuisPiece } from '../composants/PaiementDepuisPiece'
import { RefacturationPiece } from '../composants/Refacturations'
import { ProvenancePiece } from '../composants/PucesAdresses'
import { entitesPourPiece } from '../composants/RattacherPiece'
import { Section, Vide } from '../composants/Section'
import {
  LIBELLES_STATUT_PAIEMENT,
  LIBELLES_STATUT_TACHE,
  LIBELLES_TYPE_PIECE,
  options,
} from '../libelles'
import { BadgeStatutPiece } from './Pieces'
import { confirmer } from '../composants/confirmation'

const REGLEMENT: Record<string, string> = {
  prelevement: 'Prélèvement',
  virement: 'Virement',
  carte: 'Carte',
  cheque: 'Chèque',
  deja_paye: 'Déjà payée',
  inconnu: 'Non précisé',
}

export function PieceFiche({ id, vue }: { id: string; vue?: VuePieces }) {
  const client = useQueryClient()
  const navigate = useNavigate()
  const moi = useQuery(requeteMoi).data
  const fiche = useQuery({
    ...requetes.piece(id),
    // Tant que la lecture, le rangement ou l'envoi tournent côté worker, l'état se rafraîchit seul.
    refetchInterval: (q) =>
      q.state.data?.etat === 'en_lecture' || q.state.data?.etat === 'en_cours' ? 5_000 : false,
  })
  const entites = useQuery(requetes.entites)
  const file = useFilePieces(id, vue)
  const [erreur, setErreur] = useState<string | null>(null)
  const param = { param: { id } }

  if (fiche.isPending)
    return (
      <main>
        <Vide>Chargement…</Vide>
      </main>
    )
  if (!fiche.data)
    return (
      <main>
        <p className="alerte">Pièce introuvable.</p>
        <Link to="/pieces">Retour aux pièces</Link>
      </main>
    )
  const p = fiche.data
  const x = p.extraction
  const ecrivables = entitesPourPiece(p, entites.data ?? [], moi)
  const ecriture =
    moi !== undefined &&
    (p.entiteId ? ecrivables.some((e) => e.id === p.entiteId) : moi.administrateur)
  const montant = (cts: number | null | undefined) =>
    cts === null || cts === undefined ? null : formaterMontant(cts, p.devise)
  // Lecture fausse (montant, émetteur, dates) : chaque ligne se corrige sur place.
  const edition = (quoi: string, champs: Champ[]) => ({
    libelle: quoi,
    champs,
    modifiable: ecriture,
    onEnregistrer: async (v: Record<string, unknown>) => {
      await lire(api.pieces[':id'].$patch({ ...param, json: corps(pieceCorrection, v) }))
      await client.invalidateQueries()
    },
  })

  // Supprimable tant qu'elle n'a laissé aucune trace ailleurs (le service le revérifie) ;
  // une version remplie, tant qu'elle n'est pas validée.
  const brouillon = p.remplissage === 'brouillon'
  const supprimable =
    (brouillon && !p.rangee) ||
    (p.statut !== 'rattachee' &&
      !p.rangee &&
      !p.echeanceId &&
      p.transmission !== 'faite' &&
      p.paiements.length === 0 &&
      p.versions.length === 0)

  async function validerRemplissage() {
    setErreur(null)
    try {
      await lire(api.pieces[':id']['valider-remplissage'].$post(param))
      await client.invalidateQueries()
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Validation impossible.')
    }
  }

  async function agir(action: 'rejeter' | 'relancer') {
    setErreur(null)
    try {
      await lire(api.pieces[':id'].action.$post({ ...param, json: { action } }))
      await client.invalidateQueries()
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Action impossible.')
    }
  }

  async function supprimer() {
    if (
      !(await confirmer(
        'Supprimer cette pièce et son fichier ? Elle ne pourra pas être retrouvée.',
      ))
    )
      return
    setErreur(null)
    try {
      await lire(api.pieces[':id'].$delete(param))
      // Dans une file : la pièce voisine ; sinon retour à la liste.
      const voisine = file?.suivante ?? file?.precedente
      if (voisine) file?.aller(voisine)
      else await navigate({ to: '/pieces', search: { vue } })
      client.removeQueries({ queryKey: requetes.piece(id).queryKey })
      await client.invalidateQueries()
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Suppression impossible.')
    }
  }

  return (
    <main className="fiche-avec-apercu">
      <FilDrivePiece pieceId={id} ecriture={ecriture} />
      {file && <NavigationFile file={file} objet="pièce" />}
      <header className="entete-fiche">
        <p className="fil">
          <Link to="/pieces" search={{ vue }}>
            Pièces
          </Link>
          {p.entiteId && (
            <>
              {' · '}
              <Link to="/entites/$id" params={{ id: p.entiteId }}>
                {p.entite}
              </Link>
            </>
          )}
        </p>
        <h1 className="titre-logo">
          <LogoConnu nom={p.emetteur ?? x?.emetteur.nom} taille={36} />
          {p.emetteur ?? x?.emetteur.nom ?? p.nomFichier ?? 'Pièce'}
        </h1>
        <p>
          <span className="badge">{LIBELLES_TYPE_PIECE[p.type]}</span> <BadgeStatutPiece p={p} />{' '}
          <CopierLien objet="de la pièce" />
        </p>
      </header>
      {p.motifs.length > 0 && (
        <div className="alerte">
          À vérifier :
          <ul>
            {p.motifs.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="fiche-piece">
        <aside className="fiche-piece-apercu">
          <ApercuDocument
            f={{
              url: `/api/pieces/${id}/fichier`,
              nom: p.nomFichier ?? 'Pièce',
              typeMime: p.typeMime,
            }}
            plage={
              p.fichier && p.pageDebut !== null && p.pageFin !== null
                ? { debut: p.pageDebut, fin: p.pageFin }
                : null
            }
          />
        </aside>
        <div className="fiche-piece-colonne">
          {p.tva && <p className="discret">{p.tva}</p>}
          {p.fichier && (
            <Section titre="Factures de ce fichier">
              <p className="discret">
                Le PDF d’origine en regroupe plusieurs : il reste entier. Le paiement, le rangement
                dans le Drive et le dépôt chez le cabinet portent sur le fichier
                {typeof p.montantsFichier?.ttc === 'number' &&
                  `, soit ${formaterMontant(p.montantsFichier.ttc, p.devise)}`}
                .
              </p>
              <ul>
                {p.fichier.factures.map((f) => (
                  <li key={f.id}>
                    {f.id === id ? (
                      <strong>{f.numero ?? 'Sans numéro'}</strong>
                    ) : (
                      <Link to="/pieces/$id" params={{ id: f.id }}>
                        {f.numero ?? 'Sans numéro'}
                      </Link>
                    )}
                    {f.emetteurNom && ` · ${f.emetteurNom}`}
                    {typeof f.montants?.ttc === 'number' &&
                      ` · ${formaterMontant(f.montants.ttc, p.devise)}`}
                    <span className="discret">
                      {' '}
                      (p. {f.pageDebut}
                      {f.pageFin !== f.pageDebut && ` à ${f.pageFin}`})
                    </span>
                  </li>
                ))}
              </ul>
            </Section>
          )}
          {p.remplieDepuisId ? (
            <Section titre="Version remplie">
              <p>
                Remplie par l’assistant d’après{' '}
                <Link to="/pieces/$id" params={{ id: p.remplieDepuisId }}>
                  la pièce d’origine
                </Link>
                .{' '}
                {brouillon
                  ? 'À relire en regard : une fois validée, elle est rangée dans le Drive à côté de l’originale. Signer, envoyer ou déposer le formulaire reste à faire par vous.'
                  : 'Validée : rangée dans le Drive à côté de l’originale.'}
              </p>
              {brouillon && ecriture && (
                <div className="boutons">
                  <button type="button" onClick={() => void validerRemplissage()}>
                    Valider la version remplie
                  </button>
                  <button type="button" className="bouton-discret" onClick={() => void supprimer()}>
                    Abandonner
                  </button>
                </div>
              )}
              {erreur && <p className="alerte">{erreur}</p>}
            </Section>
          ) : (
            <AssistantPiece
              p={p}
              ecriture={ecriture}
              ecrivables={ecrivables}
              supprimable={supprimable}
              onRejeter={() => void agir('rejeter')}
              onSupprimer={() => void supprimer()}
            />
          )}

          {p.versions.length > 0 && (
            <Section titre="Versions remplies">
              {p.versions.map((v) => (
                <Link
                  key={v.id}
                  to="/pieces/$id"
                  params={{ id: v.id }}
                  className="ligne ligne-lien"
                >
                  <div className="ligne-contenu">
                    {v.nomFichier ?? 'Version remplie'}{' '}
                    <span className="discret">
                      {v.remplissage === 'brouillon' ? 'à valider' : 'validée'} ·{' '}
                      {formaterInstant(v.recueLe)}
                    </span>
                  </div>
                </Link>
              ))}
            </Section>
          )}

          <Section titre="Pièce">
            <dl className="details">
              <dt>Fichier</dt>
              <dd>
                <a href={`/api/pieces/${id}/fichier`} target="_blank" rel="noreferrer">
                  {p.nomFichier ?? 'Ouvrir'}
                </a>
                {p.lienDrive && (
                  <>
                    {' · '}
                    <a href={p.lienDrive} target="_blank" rel="noreferrer">
                      dans le Drive
                    </a>
                  </>
                )}
                {p.nomOriginal && p.nomOriginal !== p.nomFichier && (
                  <div className="discret">Arrivée sous le nom {p.nomOriginal}</div>
                )}
              </dd>
              <dt>Reçue</dt>
              <dd>
                {formaterInstant(p.recueLe)} · <ProvenancePiece p={p} />
                {p.expediteur && ` · ${p.expediteur}`}
                {p.objetMail && <div className="discret">{p.objetMail}</div>}
                {p.rendueDepuisMail && (
                  <div className="discret">
                    PDF tiré du corps du mail (consigne « le mail vaut pièce ») : une copie du mail,
                    pas un fichier émis par l’expéditeur.
                  </div>
                )}
              </dd>
              {p.indication && (
                <>
                  <dt>Indication</dt>
                  <dd>« {p.indication} »</dd>
                </>
              )}
              {(p.carteReseau || p.payeurAttendu) && (
                <>
                  <dt>Payée avec</dt>
                  <dd>
                    {(() => {
                      const carte = carteValide({ reseau: p.carteReseau, fin: p.carteFin })
                      return carte ? `la carte ${libelleCarte(carte)}` : 'carte non lue'
                    })()}
                    {p.payeurAttendu && (
                      <div className="discret">
                        Qui l’a payée et à qui la refacturer : décision ouverte dans ses tâches.
                      </div>
                    )}
                  </dd>
                </>
              )}
              {(x || ecriture) && (
                <>
                  <dt>Émetteur</dt>
                  <dd>
                    <EnLigne
                      {...edition('l’émetteur', [
                        { nom: 'emetteurNom', libelle: 'Émetteur', type: 'texte' },
                        { nom: 'emetteurSiren', libelle: 'SIREN', type: 'texte' },
                      ])}
                      initial={{ emetteurNom: x?.emetteur.nom, emetteurSiren: x?.emetteur.siren }}
                    >
                      {x?.emetteur.nom ?? 'Non lu'}
                      {x?.emetteur.siren && (
                        <span className="discret"> · SIREN {x.emetteur.siren}</span>
                      )}
                    </EnLigne>
                  </dd>
                  <dt>Destinataire</dt>
                  <dd>
                    <EnLigne
                      {...edition('le destinataire', [
                        { nom: 'destinataireNom', libelle: 'Destinataire', type: 'texte' },
                        { nom: 'destinataireSiren', libelle: 'SIREN', type: 'texte' },
                      ])}
                      initial={{
                        destinataireNom: x?.destinataire.nom,
                        destinataireSiren: x?.destinataire.siren,
                      }}
                    >
                      {x?.destinataire.nom ?? 'Non lu'}
                      {x?.destinataire.siren && (
                        <span className="discret"> · SIREN {x.destinataire.siren}</span>
                      )}
                    </EnLigne>
                  </dd>
                  {(x?.numero || ecriture) && (
                    <>
                      <dt>Numéro</dt>
                      <dd>
                        <EnLigne
                          {...edition('le numéro', [
                            { nom: 'numero', libelle: 'Numéro', type: 'texte' },
                          ])}
                          initial={{ numero: x?.numero }}
                        >
                          {x?.numero ?? 'Aucun'}
                        </EnLigne>
                      </dd>
                    </>
                  )}
                  <dt>Dates</dt>
                  <dd>
                    <EnLigne
                      {...edition('les dates', [
                        { nom: 'dateDocument', libelle: 'Date du document', type: 'date' },
                        { nom: 'dateEcheance', libelle: 'Échéance', type: 'date' },
                      ])}
                      initial={{ dateDocument: x?.dateDocument, dateEcheance: x?.dateEcheance }}
                    >
                      {x?.dateDocument ? formaterJour(x.dateDocument) : 'Non lue'}
                      {x?.dateEcheance && `, échéance le ${formaterJour(x.dateEcheance)}`}
                    </EnLigne>
                  </dd>
                  <dt>Montants</dt>
                  <dd>
                    <EnLigne
                      {...edition('les montants', [
                        { nom: 'htCts', libelle: 'HT', type: 'montant' },
                        { nom: 'tvaCts', libelle: 'TVA', type: 'montant' },
                        { nom: 'ttcCts', libelle: 'TTC', type: 'montant' },
                        { nom: 'aPayerCts', libelle: 'À payer', type: 'montant' },
                      ])}
                      initial={{ ...x?.montants }}
                    >
                      {(x &&
                        [
                          montant(x.montants.htCts) && `${montant(x.montants.htCts)} HT`,
                          montant(x.montants.tvaCts) && `TVA ${montant(x.montants.tvaCts)}`,
                          montant(x.montants.ttcCts) && `${montant(x.montants.ttcCts)} TTC`,
                          montant(x.montants.aPayerCts) &&
                            `à payer ${montant(x.montants.aPayerCts)}`,
                        ]
                          .filter(Boolean)
                          .join(' · ')) ||
                        'Aucun'}
                    </EnLigne>
                  </dd>
                  {(x?.objet || ecriture) && (
                    <>
                      <dt>Objet</dt>
                      <dd>
                        <EnLigne
                          {...edition('l’objet', [
                            { nom: 'objet', libelle: 'Objet', type: 'texte' },
                          ])}
                          initial={{ objet: x?.objet }}
                        >
                          {x?.objet ?? 'Aucun'}
                        </EnLigne>
                      </dd>
                    </>
                  )}
                  <dt>Règlement</dt>
                  <dd>
                    <EnLigne
                      {...edition('le règlement', [
                        {
                          nom: 'modeReglement',
                          libelle: 'Règlement',
                          type: 'choix',
                          requis: true,
                          options: options(REGLEMENT),
                        },
                      ])}
                      initial={{ modeReglement: x?.modeReglement ?? 'inconnu' }}
                    >
                      {REGLEMENT[x?.modeReglement ?? 'inconnu']}
                    </EnLigne>
                  </dd>
                  {x && (
                    <>
                      <dt>Lecture</dt>
                      <dd>
                        {x.corrigee
                          ? 'Corrigée à la main'
                          : x.confiance === 1
                            ? 'XML structuré (Factur-X, UBL)'
                            : `Claude, confiance ${p.confiance} %`}
                      </dd>
                    </>
                  )}
                </>
              )}
              {p.echeanceId && (
                <>
                  <dt>Échéance</dt>
                  <dd>
                    <Link to="/echeances/$id" params={{ id: p.echeanceId }}>
                      Voir l’échéance
                    </Link>
                  </dd>
                </>
              )}
              {p.doublonDeId && (
                <>
                  <dt>Doublon de</dt>
                  <dd>
                    <Link to="/pieces/$id" params={{ id: p.doublonDeId }}>
                      la pièce déjà reçue
                    </Link>
                  </dd>
                </>
              )}
            </dl>
            {ecriture && p.statut !== 'rattachee' && (
              <div className="boutons">
                <button type="button" className="bouton-discret" onClick={() => agir('relancer')}>
                  Relire
                </button>
              </div>
            )}
            {erreur && <p className="alerte">{erreur}</p>}
          </Section>

          {!p.remplieDepuisId && <PaiementDepuisPiece pieceId={id} />}

          {!p.remplieDepuisId && p.entiteId && ecriture && p.statut !== 'ignoree' && (
            <RefacturationPiece pieceId={id} entiteId={p.entiteId} />
          )}

          {(p.source === 'gmail' || p.remplieDepuisId) && (
            <Section titre="Échanges">
              <FilsPiece pieceId={id} />
            </Section>
          )}

          {(p.paiements.length > 0 || p.taches.length > 0) && (
            <Section titre="Effets">
              {p.paiements.map((pa) => (
                <div key={pa.id} className="ligne">
                  <div className="ligne-contenu">
                    Paiement {pa.sens === 'payer' ? 'à payer' : 'à encaisser'} : {pa.libelle ?? ''}{' '}
                    <span className="discret">{LIBELLES_STATUT_PAIEMENT[pa.statut]}</span>
                  </div>
                  <span className="montant">{formaterMontant(pa.montantDuCts, pa.devise)}</span>
                </div>
              ))}
              {p.taches.map((t) => (
                <Link
                  key={t.id}
                  to="/taches/$id"
                  params={{ id: t.id }}
                  className="ligne ligne-lien"
                >
                  <div className="ligne-contenu">
                    {t.titre} <span className="discret">{LIBELLES_STATUT_TACHE[t.statut]}</span>
                  </div>
                </Link>
              ))}
            </Section>
          )}

          <Section titre="Historique">
            <Historique objetId={id} />
          </Section>
        </div>
      </div>
    </main>
  )
}
